import styled from '@emotion/styled';
import { Card as _Card, Chip as _Chip, Typography } from '@material-ui/core';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import { createElement, FC } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  CreationSet,
  CreationState,
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatPlaybackTime,
  MatchingFormat,
  Organization,
} from '../../../graphql/generated/graphql';
import { SingleOfferFragment } from '../../../graphql/generated/graphql_offer';
import { ProjectType } from '../../../service/persistent/ProjectWebApi';
import OrganizationNameWithIcon from '../../component/primitive/OrganizationNameWithIcon';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import CreationStateMessage from './CreationStateMessage';
import DeliveryDueDate from './DeliveryDueDate';
import _CreativeFormatDisplay from './ProjectsList/CreativeFormatDisplay';
import UploadedItemCount from './UploadedItemCount';

type Props = {
  project: Pick<ProjectType, 'title' | 'matchingFormat'> & {
    organization: Pick<Organization, 'name'>;
  };
  targetCreationSet: Pick<CreationSet, 'expressDueDays'>;
  offer: Pick<SingleOfferFragment, 'expectedDue'>;
  passedDeadline: boolean;
  creativeCount: number;
  creativeFormat: {
    network: Pick<CreativeFormat['network'], 'key'>;
    placement: Pick<CreativeFormat['placement'], 'key' | 'name'>;
    creativeType: Pick<CreativeFormat['creativeType'], 'key'>;
    attribute: {
      playbackTime?: {
        seconds: CreativeFormatPlaybackTime['seconds'];
      } | null;
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
    };
    resizeBaseId?: CreativeFormat['resizeBaseId'];
  };
  baseCreativeFormat: {
    network: Pick<CreativeFormat['network'], 'key'>;
    placement: Pick<CreativeFormat['placement'], 'key' | 'name'>;
    creativeType: Pick<CreativeFormat['creativeType'], 'key'>;
    attribute: {
      playbackTime?: {
        seconds: CreativeFormatPlaybackTime['seconds'];
      } | null;
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
    };
    resizeBaseId?: CreativeFormat['resizeBaseId'];
  } | null;
  resizeCreativeFormats: {
    network: Pick<CreativeFormat['network'], 'key'>;
    placement: Pick<CreativeFormat['placement'], 'key' | 'name'>;
    creativeType: Pick<CreativeFormat['creativeType'], 'key'>;
    attribute: {
      playbackTime?: {
        seconds: CreativeFormatPlaybackTime['seconds'];
      } | null;
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
    };
    resizeBaseId?: CreativeFormat['resizeBaseId'];
  }[];
  creationState: CreationState | null;
  className?: string;
};

const ProjectSummary: FC<Props> = ({
  project,
  targetCreationSet,
  offer,
  passedDeadline,
  creativeCount,
  creativeFormat,
  baseCreativeFormat,
  resizeCreativeFormats,
  creationState,
  className,
}) => {
  return (
    <Root className={className}>
      <Title variant="h4" color="primary">
        {project.title}
      </Title>
      <ChipAndOrganization>
        {project.matchingFormat == MatchingFormat.Competition && (
          <CompetitionChip
            label={<FormattedMessage id="show_creation_page.label.competition" />}
            variant="outlined"
            color="primary"
            icon={<PeopleOutlineIcon />}
          />
        )}
        <OrganizationName name={project.organization.name || ''} />
      </ChipAndOrganization>
      <CreativeCount count={creativeCount} />
      {creationState ? (
        <StyledCreationStateMessage latestDeliverableModificationRequest={null} creationState={creationState} />
      ) : null}
      <DueDates>
        <DeliveryDueDate dueDate={offer.expectedDue} />
        {targetCreationSet.expressDueDays ? (
          <Chip label={<FormattedMessage id="show_creation_page.express" />} />
        ) : null}
      </DueDates>
      {passedDeadline && project.matchingFormat == 'competition' && (
        <CannotUploadCreativeMessage color="error">
          <FormattedMessage id="show_creation_page.message.cannot_upload" />
        </CannotUploadCreativeMessage>
      )}
      <StyledCreativeFormatDisplay>
        <Card>
          <CardContent>
            <CreativeFormatDisplay creativeFormat={creativeFormat} />
          </CardContent>
        </Card>

        {baseCreativeFormat ? (
          <Card>
            <CardContent>
              <SubHeading variant="body1">
                <FormattedMessage id="show_creation_page.project_summary.resize_base" />
              </SubHeading>
              <CreativeFormatDisplay creativeFormat={baseCreativeFormat} />
            </CardContent>
          </Card>
        ) : null}

        {resizeCreativeFormats.length > 0 ? (
          <Card>
            <CardContent>
              <SubHeading variant="body1">
                <FormattedMessage id="show_creation_page.project_summary.resize_item" />
              </SubHeading>
              {resizeCreativeFormats.map((resizeCreativeFormat, index) => (
                <CreativeFormatDisplay key={index} creativeFormat={resizeCreativeFormat} />
              ))}
            </CardContent>
          </Card>
        ) : null}
      </StyledCreativeFormatDisplay>
    </Root>
  );
};

export const Root = styled('div')(
  gridLayoutContainer({
    columns: ['auto', 'auto', 'auto', '1fr'],
    rows: ['auto', 'auto', `${SPACING_REGULAR}px`, 'auto', `${SPACING_REGULAR}px`, 'auto'],
    areas: [
      ['title', 'title', 'title', 'title'],
      ['chip_and_organization', 'chip_and_organization', 'chip_and_organization', 'chip_and_organization'],
      ['.', '.', '.', '.'],
      ['creative_count', 'state', 'delivery_due_date', 'cannot_upload_message'],
      ['.', '.', '.', '.'],
      ['creative_format', 'creative_format', 'creative_format', 'creative_format'],
    ],
  }),
  {
    alignItems: 'center',
  },
);

const Title = styled(Typography)(gridLayoutItem('title'), {
  wordWrap: 'break-word',
  wordBreak: 'break-word' as any,
  marginBottom: SPACING_UNIT,
});

const OrganizationName = styled(OrganizationNameWithIcon)({});

const CreativeCount = styled(UploadedItemCount)(gridLayoutItem('creative_count'), { paddingRight: SPACING_REGULAR });

const StyledCreationStateMessage = styled(CreationStateMessage)(gridLayoutItem('state'), {
  paddingRight: SPACING_HUGE,
});

const StyledCreativeFormatDisplay = styled('div')(gridLayoutItem('creative_format'), {});

const DueDates = styled('div')(gridLayoutItem('delivery_due_date'), {
  ...flexibleLayoutContainer({
    direction: 'row',
    alignItems: 'center',
  }),
});

const CannotUploadCreativeMessage = styled(Typography)(gridLayoutItem('cannot_upload_message'), {
  marginLeft: SPACING_UNIT * 2,
  fontSize: '0.8125rem',
  fontWeight: 500,
});

const Card = styled(_Card)({
  marginBottom: SPACING_REGULAR,
  ':last-child': {
    marginBottom: 0,
  },
});

const CardContent = styled('div')({
  padding: '16px 24px',
});

const SubHeading = styled(Typography)({
  marginBottom: SPACING_REGULAR,
});

const CreativeFormatDisplay = styled(_CreativeFormatDisplay)({
  marginBottom: SPACING_UNIT * 2,
  ':last-child': {
    marginBottom: 0,
  },
});

const Chip = styled(_Chip)({
  marginLeft: SPACING_REGULAR / 2,
});

const CompetitionChip = styled(_Chip)({
  marginRight: SPACING_REGULAR,
  minWidth: 100,
});

const ChipAndOrganization = styled('div')(gridLayoutItem('chip_and_organization'), {
  display: 'flex',
});

export default ProjectSummary;
